import Text from 'components/shared/Text'
import { FormattedMessage, useIntl } from 'react-intl'
import Link from 'components/shared/Link'
import Box from 'components/shared/Box'
import Input from 'components/shared/Input'
import { removeVoucher, setVoucherAmount } from 'store/vouchers-slice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useEffect, useState } from 'react'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import { useAutoScroll } from 'utils/hooks'
import { Wrapper } from './styles'
import VoucherValueRadio from 'routes/vouchers/VouchersConfiguration/VoucherValueRadio'
import PersonalisedVoucher from 'routes/vouchers/VouchersConfiguration/PersonalisedVoucher'

const SUGGESTED_OPTIONS = [100, 300, 500, null]
const MAX_AMOUNT = 9999

interface ValueVoucherConfigurationProps {
    index: number
    isEraseable?: boolean
}

const ValueVoucherConfiguration = ({
    index,
    isEraseable = true,
}: ValueVoucherConfigurationProps) => {
    const dispatch = useAppDispatch()
    const { formatMessage } = useIntl()
    const { getError, handleRemoveError } = useErrorMessages()
    const { animatePendingScroll, scrollIndex } = useAutoScroll()

    const { vouchersBoxes } = useAppSelector((state) => state.vouchers)
    const { amount } = vouchersBoxes[index]

    const [selectedOption, setSelectedOption] = useState<
        number | null | undefined
    >()

    const sectionId = `voucher-${index}`

    const handleRemoveTicket = () => dispatch(removeVoucher(index))

    const handleChangeOption = (option: number | null) => {
        setSelectedOption(option)
        dispatch(
            setVoucherAmount({
                index,
                amount: option || 0,
            })
        )
    }

    const handleChangeValue = (value: string) => {
        const maxValueLength = MAX_AMOUNT.toString().length
        const trimmedValue = value.substring(0, maxValueLength)
        const formattedValue = trimmedValue.replace(/[^0-9]/g, '')

        handleRemoveError(`items[${index}].detail.amount`)
        dispatch(
            setVoucherAmount({
                index,
                amount: parseInt(formattedValue || '0', 10),
            })
        )
    }

    useEffect(() => {
        animatePendingScroll(sectionId)
    }, [scrollIndex])

    useEffect(() => {
        if (SUGGESTED_OPTIONS.includes(amount || null))
            return setSelectedOption(amount)
        if (amount !== 0) return setSelectedOption(null)
    }, [])

    return (
        <Wrapper id={sectionId}>
            <Box
                direction="row"
                align="center"
                justify="space-between"
                mb={[32, 40]}
            >
                <Text variant="heading2" color="grayscale_900">
                    <FormattedMessage
                        id="vouchers.config.item_caption"
                        defaultMessage="Voucher"
                    />{' '}
                    {index + 1}
                </Text>

                {isEraseable && (
                    <Link
                        variant="secondary"
                        icon="bin"
                        onClick={() => handleRemoveTicket()}
                    >
                        <FormattedMessage
                            id="global.btn.remove"
                            defaultMessage="Usuń"
                        />
                    </Link>
                )}
            </Box>
            <Text variant="heading3" mb={[16, 24]}>
                <FormattedMessage
                    id="vouchers.config.value_item_choose_caption"
                    defaultMessage="Wybierz kwotę Vouchera"
                />
            </Text>
            <Box
                isFullWidth
                direction={['column', 'row']}
                gap={[8, 16]}
                mb={16}
            >
                {SUGGESTED_OPTIONS.map((option) => (
                    <VoucherValueRadio
                        option={option}
                        id={`voucher-${index}-option-${option}`}
                        isChecked={option === selectedOption}
                        onClick={() => handleChangeOption(option)}
                        key={`voucher-${index}-option-${option}`}
                        testId={`voucher-${index}-select-amount-${
                            option || 'custom'
                        }`}
                    >
                        {option ? (
                            <>
                                <Text variant="bodyLarge" color="grayscale_900">
                                    {option}{' '}
                                    <Text
                                        variant="bodyLarge"
                                        color="primary_500"
                                        as="span"
                                    >
                                        PLN
                                    </Text>
                                </Text>
                            </>
                        ) : (
                            <Text variant="bodyLarge" color="grayscale_900">
                                <FormattedMessage
                                    id="vouchers.config.value_item_other_value"
                                    defaultMessage="Inna kwota"
                                />
                            </Text>
                        )}
                    </VoucherValueRadio>
                ))}
            </Box>
            {selectedOption === null && (
                <Input
                    type="text"
                    id={`items[${index}].detail.amount`}
                    onBlurChange={(value) => handleChangeValue(value)}
                    onValueChange={(value) => handleChangeValue(value)}
                    label={formatMessage({
                        id: 'vouchers.config.value_item_other_value_label',
                        defaultMessage: 'Kwota vouchera',
                    })}
                    value={amount ? amount.toString() : ''}
                    maxWidth={308}
                    isLargePaddingRight
                    suffix="PLN"
                    isError={!!getError(`items[${index}].detail.amount`)}
                    errorMessage={getError(`items[${index}].detail.amount`)}
                    testId={`voucher-${index}-field-custom-amount`}
                />
            )}

            <PersonalisedVoucher index={index} />
        </Wrapper>
    )
}

export default ValueVoucherConfiguration
